import type { QuestionFull } from "../hooks/useQuestion";
import { api } from "./api";
import { preserveLocalAnswer } from "./questionProgress";
import { getAnswer as getLocalAnswer } from "./localAnswers";
import { createQuestionStore, type QuestionStore } from "./questionStore";

/**
 * 單題 payload 的應用層快取。`peek()` 同步可讀,所以預抓命中時換題完全不進
 * loading 狀態。失效由呼叫端掌握:存檔後 `set()` 覆寫、`reload({force})` 繞過 ttl。
 *
 * ttl 60s 是刻意的短命:詳解是共筆,別人可能剛改過。過期不代表丟掉 —— 仍然先把
 * 舊的畫出來(stale-while-revalidate),同時背景重抓。
 */
export const questionCache: QuestionStore<QuestionFull> = createQuestionStore<QuestionFull>(async (id) => {
	const fresh = await api.get<QuestionFull>(`/api/questions/${id}`);
	// 掛在 fetcher 上,因為這是**每一條**重抓路徑的唯一交會點 —— 背景重抓、
	// reload({force}),以及最容易被忽略的那條:離開這一題時、鄰居頁的閒置預抓。
	// 見 preserveLocalAnswer 的說明(#95 的後半)。
	// 「本地那份」有兩個來源,記憶體優先(它最完整),localStorage 墊底。
	// 少了後者,保護會剛好在最需要的時候失效:整頁重載後記憶體是空的,而使用者
	// 的實際動作正好包含重載(關掉分頁、隔天再開)。見 lib/localAnswers.ts。
	return preserveLocalAnswer(fresh, questionCache.peek(id) ?? localHint(fresh, id));
});

/**
 * 用 localStorage 裡「我答過什麼」拼一份最小的 local payload,只為了讓
 * `preserveLocalAnswer` 有東西可比。其餘欄位一律沿用伺服器回來的 —— 這裡要救的
 * 只有作答狀態,不是整份題目。
 */
function localHint(fresh: QuestionFull, id: string): QuestionFull | undefined {
	const mine = getLocalAnswer(id);
	if (!mine) return undefined;
	const p = fresh.my_progress;
	return {
		...fresh,
		my_progress: {
			times_seen: p?.times_seen ?? 1,
			times_correct: p?.times_correct ?? mine.correct,
			last_chosen: mine.chosen,
			last_correct: mine.correct,
			// 收藏跟作答是兩回事,一律以伺服器為準 —— 本地鏡像根本不記它。
			bookmarked: p?.bookmarked ?? 0,
			bookmark_folder_id: p?.bookmark_folder_id ?? null,
		},
	};
}

export type YearListItem = { id: string; number: number };

/**
 * 「同年度題目清單」—— 上一題/下一題就是靠它算出來的。以前每換一題都重抓一次,
 * 等於每次導覽多一趟 RTT,而且是**在題目載入之後**才發,所以 prev/next 按鈕會慢
 * 半拍才亮。一年的題目清單只有發布新年份時才變,快取五分鐘綽綽有餘。
 */
export const yearListCache = createQuestionStore<YearListItem[]>(
	(year) => api.get<YearListItem[]>(`/api/questions?year=${year}&limit=200`),
	{ max: 12, ttlMs: 5 * 60_000 },
);
